import { useId, useState, type ReactNode } from "react";
import { AlertTriangle } from "./icons";
import { Button } from "./primitives";

/**
 * The mandatory reason an irreversible action captures before it commits (constitution VI).
 *
 * Shared rather than written twice: `003` captures a reason on demotion and decline, `004` on
 * rejection, cancellation and short fulfilment, `011` on e-way bill and IRN cancellation. The
 * backend logs the reason immutably, and a blank reason is useless six months later.
 *
 * ## It is a native `<dialog>` with a real `<form>`, and no client directive
 *
 * The reason field is `required`, so the browser refuses the submit and points at the empty field
 * with no JavaScript of ours (constitution VII). The form posts to the Server Action the caller
 * passes as `action`; this component owns the *contract* — an irreversible action does not commit
 * without a reason — not the mutation.
 *
 * `open` renders the dialog open in the page. **Modality is the app's business**: a `showModal()`
 * call needs a ref and a client boundary, and `packages/ui` carries no `"use client"` (R5). Apps
 * that want the top layer render this from an intercepting route or attach `showModal` in their own
 * client component; apps that do not get a perfectly serviceable inline confirmation panel.
 *
 * ## `consequence` is not optional
 *
 * "Are you sure?" is not a confirmation, it is a speed bump. The dialog says what will happen and
 * what cannot be undone, in the API's or the screen's own words.
 */
export function ReasonDialog({
  title,
  consequence,
  action,
  confirmLabel,
  cancel,
  reasonLabel = "Reason",
  reasonName = "reason",
  reasonHint = "Recorded in the audit log and shown to whoever reviews this later.",
  error,
  open = true,
  children,
}: {
  title: string;
  /** What this does and what it cannot undo. Required on purpose. */
  consequence: ReactNode;
  /** The Server Action, or a route. This component performs no mutation of its own. */
  action: React.ComponentProps<"form">["action"];
  /** Names the act — "Demote to Default Tier", not "OK". */
  confirmLabel: string;
  /** The way out. A confirmation with no exit is a trap. */
  cancel: ReactNode;
  reasonLabel?: string;
  reasonName?: string;
  reasonHint?: ReactNode;
  /** The API's rejection, rendered verbatim (constitution VI). */
  error?: string;
  open?: boolean;
  /** Anything else the decision needs — the affected lines, the balance, the packing photo. */
  children?: ReactNode;
}) {
  // useId, not the reason name alone: two dialogs on one page (Reject and Cancel on an order) would
  // otherwise share an id and a label could point at the other dialog's hidden textarea.
  const id = `reason-dialog-${reasonName}-${useId().replace(/:/g, "")}`;
  // Checked here rather than by the browser's own bubble (`noValidate` below): an empty or
  // whitespace-only reason gets the same inline message as every other field in the app, and
  // `required` stays on the textarea for assistive technology.
  const [missing, setMissing] = useState(false);
  const shown = missing ? "A reason is required." : error;

  return (
    <dialog
      open={open}
      data-slot="reason-dialog"
      aria-labelledby={`${id}-title`}
      className="border-muted bg-gray-0 text-foreground shadow-lg z-50 w-full max-w-lg rounded-xl border p-0"
    >
      <form
        action={action}
        noValidate
        onSubmit={(event) => {
          const reason = new FormData(event.currentTarget).get(reasonName);
          const blank = typeof reason !== "string" || reason.trim() === "";
          setMissing(blank);
          if (blank) event.preventDefault();
        }}
        className="flex flex-col gap-4 p-5"
      >
        <div className="flex gap-3.5">
          <span className="bg-red/10 text-red-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
            <AlertTriangle aria-hidden className="size-4" />
          </span>
          <div className="flex min-w-0 flex-col gap-1.5">
            <h2 id={`${id}-title`} className="text-base font-semibold">
              {title}
            </h2>
            <p className="text-muted-foreground text-sm">{consequence}</p>
          </div>
        </div>

        {children}

        <div className="flex flex-col gap-1.5">
          <label htmlFor={id} className="text-sm font-medium">
            {reasonLabel}
          </label>
          <textarea
            id={id}
            name={reasonName}
            required
            rows={3}
            aria-describedby={`${id}-hint`}
            aria-invalid={shown ? true : undefined}
            onInput={() => setMissing(false)}
            className="border-muted bg-gray-0 focus-visible:border-primary focus-visible:ring-primary/50 aria-invalid:border-red aria-invalid:ring-red/20 w-full rounded-lg border px-3 py-2 text-base shadow-sm transition-colors outline-none focus-visible:ring-3 aria-invalid:ring-3"
          />
          <p id={`${id}-hint`} className="text-muted-foreground text-xs">
            {reasonHint}
          </p>
          {shown ? (
            <p role="alert" className="mt-0.5 text-[13px] text-red">
              {shown}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {cancel}
          <Button type="submit" variant="destructive" size="lg">
            {confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
